import { ref, readonly, watch } from 'vue';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import {
  MAX_SCALE,
  MIN_SCALE,
  DEFAULT_SCALE,
  STEP,
  STEP_COUNT,
  applyScale,
  clampScale,
  isScaledRoute,
  readPreference,
  snapScale,
  writePreference,
} from '@/utils/font-scale';

/**
 * Reactive wrapper around src/utils/font-scale.js.
 *
 * Module-level refs, in the same shape as useFeatureFlags — one app-wide value
 * with one source of truth rather than a copy per component.
 *
 * `scale` is what is actually applied right now, which is 1 everywhere outside
 * Next Steps. `preference` is what the member chose, which survives leaving the
 * section. Components that measure themselves in px (CoursesCarousel) watch
 * `scale`, not `preference`.
 */

/**
 * Re-exported, because FontScaleControl imports the band from here — this module
 * is the front door to the feature and the control should not have to know that
 * the numbers live one layer down.
 *
 * They were imported above and never re-exported, which is not a link error in a
 * webpack build: the control's `import { MIN_SCALE } from '@/composables/...'`
 * simply resolved to undefined. Vue then dropped the min/max/step attributes and
 * the range input fell back to its HTML defaults of 0-100 step 1 — so one notch
 * of the reader's text size moved --font-scale from 1 to 2, blowing the root
 * font-size from 16px to 32px, and `change` clamped it back on release. See
 * tests/unit/components/FontScaleControl.spec.js.
 */
export { MIN_SCALE, MAX_SCALE, STEP, STEP_COUNT, DEFAULT_SCALE } from '@/utils/font-scale';

/** @type {import('vue').Ref<number>} the member's chosen size */
const preference = ref(1);

/** @type {import('vue').Ref<number>} the scale applied to the document */
const scale = ref(1);

/** @type {import('vue').Ref<boolean>} whether the current route is scaled */
const isActive = ref(false);

/** Last route handed to applyScaleForRoute, so a late flag load can re-run it. */
let lastPath = null;

/** bootFontScale is called once, but guard anyway — a second watcher would
 *  re-apply the scale twice on every flag change. */
let flagWatcherStarted = false;

/**
 * Called once from main.js. Only loads the stored choice — it does not apply
 * it, because the landing route is almost never a Next Steps one.
 */
export function bootFontScale() {
  preference.value = readPreference();
  scale.value = applyScale(1);

  // Flags load over the network after boot, so a cold deep link into a lesson
  // can evaluate the gate before the answer arrives. Re-run it when the flags
  // Map changes, or that member reads the whole lesson at the default size with
  // no control in sight.
  //
  // Set up here rather than at module scope: watch() runs its getter once on
  // creation, which would touch the flag layer at import time.
  if (!flagWatcherStarted) {
    flagWatcherStarted = true;
    watch(
      () => isFontScaleEnabled(),
      () => {
        if (lastPath !== null) applyScaleForRoute(lastPath);
      },
    );
  }

  return preference.value;
}

/**
 * The flag this whole feature hangs off. Gated in one place rather than at each
 * call site: with it off, applyScaleForRoute never leaves 1 on <html>, so the
 * app renders exactly as it did before this branch even for a member who has a
 * size stored from a build where the flag was on.
 *
 * There is no API half to gate — the size lives in localStorage and no endpoint
 * knows about it.
 */
export const FEATURE_FLAG = 'reader-text-size';

/**
 * Called lazily, never at module scope. Reading `flags.value` inside the call is
 * what registers the dependency, so a computed or watcher that calls this still
 * re-evaluates when the flags land — holding the composable in a module const
 * bought nothing and initialised the flag layer at import time, which broke any
 * spec that jest.mock's it.
 * @returns {boolean}
 */
export function isFontScaleEnabled() {
  return useFeatureFlags().isEnabled(FEATURE_FLAG);
}

/**
 * The drag path is coalesced onto one animation frame; everything else writes
 * straight through.
 *
 * Dragging the slider fires `input` for every step of the track, and each step
 * moves `html`'s font-size — which relayouts the whole document, because every
 * size in the app is expressed in rem. Applying that inside the event handler
 * put the reflow, the re-render of the panel and the re-measure that components
 * do in response all in the same tick, several times per frame: the reader
 * visibly strobed between sizes instead of resizing. Batching to one apply per
 * frame, last value wins, gives the browser exactly one layout per painted
 * frame — which is the most a live preview can honestly show.
 *
 * `null` rather than 0 as the empty value: 0 is a scale a caller could ask for.
 */
let previewPending = null;
let previewFrame = 0;

const canSchedule = () =>
  typeof requestAnimationFrame === 'function' && typeof cancelAnimationFrame === 'function';

function flushPreview() {
  previewFrame = 0;
  const value = previewPending;
  previewPending = null;
  if (value !== null) scale.value = applyScale(value);
}

/**
 * Drop a queued preview. Called by every path that writes synchronously, so a
 * frame queued mid-drag cannot land after the release and undo it.
 */
function cancelPreview() {
  if (previewFrame) cancelAnimationFrame(previewFrame);
  previewFrame = 0;
  previewPending = null;
}

/**
 * Apply, or clear, the scale for a route. Wired to the router's afterEach so
 * leaving Next Steps always resets — a member who set Largest to read a lesson
 * does not then find the giving flow at 150%.
 * @param {string} path
 */
export function applyScaleForRoute(path) {
  cancelPreview();
  lastPath = path;
  isActive.value = isFontScaleEnabled() && isScaledRoute(path);
  scale.value = applyScale(isActive.value ? clampScale(preference.value) : 1);
  return scale.value;
}

/**
 * Change the size. Only takes visible effect on a Next Steps route; elsewhere
 * it is stored for the next time one is opened.
 * @param {number} value
 */
export function setPreference(value) {
  cancelPreview();
  // snap, not clamp: the stored size is one of the six the slider offers, and the
  // thumb reads back off this ref — a value between two stops would leave them
  // pointing at different sizes.
  preference.value = snapScale(value);
  writePreference(preference.value);
  if (isActive.value) scale.value = applyScale(preference.value);
  return preference.value;
}

/**
 * Apply a scale WITHOUT storing it — this is the live preview behind the slider,
 * and what the dev harness uses to stress sizes past MAX_SCALE without stranding
 * anyone above the ceiling.
 *
 * Coalesced (see above), so the returned value is the size that is *about* to be
 * applied rather than one already on the document.
 * @param {number} value
 * @returns {number}
 */
export function previewScale(value) {
  previewPending = clampScale(value, Infinity);
  if (!canSchedule()) {
    flushPreview();
    return scale.value;
  }
  if (!previewFrame) previewFrame = requestAnimationFrame(flushPreview);
  return previewPending;
}

/** Drop a preview and go back to what the stored choice resolves to here. */
export function revertPreview() {
  cancelPreview();
  scale.value = applyScale(isActive.value ? clampScale(preference.value) : 1);
  return scale.value;
}

/**
 * The live refs, for Options API components.
 *
 * AGENTS.md forbids setup() in this codebase, and these are module-level refs —
 * so a component reads them straight from a computed (`fontScaleState.scale.value`)
 * with no Composition API entry point at all.
 */
export const fontScaleState = {
  preference: readonly(preference),
  scale: readonly(scale),
  isActive: readonly(isActive),
};

export function useFontScale() {
  return {
    preference: readonly(preference),
    scale: readonly(scale),
    isActive: readonly(isActive),
    STEP,
    STEP_COUNT,
    DEFAULT_SCALE,
    MIN_SCALE,
    MAX_SCALE,
    setPreference,
    previewScale,
    revertPreview,
  };
}
